import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Users, Plus, Edit, Trash2, Shield, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [availablePages, setAvailablePages] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    is_admin: false,
    allowed_pages: ["dashboard"],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, pagesRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/available-pages`),
      ]);
      setUsers(usersRes.data);
      setAvailablePages(pagesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Veriler yüklenirken hata oluştu");
    }
  };

  const handleOpenDialog = (user = null) => {
    if (user) {
      setIsEditMode(true);
      setSelectedUser(user);
      setFormData({
        username: user.username,
        email: user.email,
        password: "",
        is_admin: user.is_admin,
        allowed_pages: user.allowed_pages || ["dashboard"],
      });
    } else {
      setIsEditMode(false);
      setSelectedUser(null);
      setFormData({
        username: "",
        email: "",
        password: "",
        is_admin: false,
        allowed_pages: ["dashboard"],
      });
    }
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEditMode && selectedUser) {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await axios.put(`${API}/users/${selectedUser.id}`, updateData);
        toast.success("Kullanıcı güncellendi");
      } else {
        if (!formData.password) {
          toast.error("Şifre zorunludur");
          return;
        }
        await axios.post(`${API}/users`, formData);
        toast.success("Kullanıcı oluşturuldu");
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error saving user:", error);
      const message = error.response?.data?.detail || "İşlem başarısız";
      toast.error(message);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`${user.username} kullanıcısını silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/users/${user.id}`);
      toast.success("Kullanıcı silindi");
      fetchData();
    } catch (error) {
      console.error("Error deleting user:", error);
      const message = error.response?.data?.detail || "Silme işlemi başarısız";
      toast.error(message);
    }
  };

  const handlePageToggle = (pageId) => {
    setFormData((prev) => {
      const pages = prev.allowed_pages.includes(pageId)
        ? prev.allowed_pages.filter((p) => p !== pageId)
        : [...prev.allowed_pages, pageId];
      return { ...prev, allowed_pages: pages };
    });
  };

  const getPageName = (pageId) => {
    const page = availablePages.find((p) => p.id === pageId);
    return page ? page.name : pageId;
  };

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight" data-testid="user-management-title">
            Kullanıcı Yönetimi
          </h1>
          <p className="text-base text-muted-foreground mt-2">
            Kullanıcıları yönetin ve erişim izinlerini ayarlayın
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-user-btn">
          <Plus className="w-4 h-4 mr-2" />
          Yeni Kullanıcı
        </Button>
      </div>

      {/* Users List */}
      <Card data-testid="users-list">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Kullanıcılar ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 font-semibold text-sm">Kullanıcı Adı</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">E-posta</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Rol</th>
                  <th className="text-left py-3 px-4 font-semibold text-sm">Erişim İzinleri</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">Durum</th>
                  <th className="text-center py-3 px-4 font-semibold text-sm">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-border table-row" data-testid={`user-row-${user.id}`}>
                    <td className="py-3 px-4 font-medium">{user.username}</td>
                    <td className="py-3 px-4 text-muted-foreground">{user.email}</td>
                    <td className="py-3 px-4 text-center">
                      {user.is_admin ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 font-medium">
                          <ShieldCheck className="w-3 h-3" />
                          Admin
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-700 font-medium">
                          <Shield className="w-3 h-3" />
                          Kullanıcı
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-wrap gap-1">
                        {user.is_admin ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-purple-50 text-purple-600">
                            Tüm Sayfalar
                          </span>
                        ) : (
                          user.allowed_pages?.map((pageId) => (
                            <span
                              key={pageId}
                              className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-600"
                            >
                              {getPageName(pageId)}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      {user.is_active ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700 font-medium">
                          Aktif
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium">
                          Devre Dışı
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(user)}
                          data-testid={`edit-user-${user.id}`}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(user)}
                          data-testid={`delete-user-${user.id}`}
                          className="text-destructive hover:text-destructive hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="text-center py-12" data-testid="no-users">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Henüz kullanıcı bulunmamaktadır</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit User Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? "Kullanıcıyı Düzenle" : "Yeni Kullanıcı Ekle"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="username">Kullanıcı Adı *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="ornek_kullanici"
                  data-testid="user-username-input"
                  required
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="email">E-posta *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="ornek@email.com"
                  data-testid="user-email-input"
                  required
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="password">
                  Şifre {isEditMode ? "(boş bırakılırsa değişmez)" : "*"}
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={isEditMode ? "Yeni şifre (opsiyonel)" : "Şifre girin"}
                  data-testid="user-password-input"
                  required={!isEditMode}
                />
              </div>

              <div className="col-span-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="is_admin"
                    checked={formData.is_admin}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_admin: checked })}
                    data-testid="user-admin-checkbox"
                  />
                  <Label htmlFor="is_admin" className="flex items-center gap-2 cursor-pointer">
                    <ShieldCheck className="w-4 h-4 text-purple-600" />
                    Admin yetkisi ver (Tüm sayfalara erişim)
                  </Label>
                </div>
              </div>

              {!formData.is_admin && (
                <div className="col-span-2">
                  <Label className="mb-3 block">Erişim İzinleri</Label>
                  <div className="grid grid-cols-2 gap-3 p-4 bg-secondary rounded-lg">
                    {availablePages.map((page) => (
                      <div key={page.id} className="flex items-start space-x-2">
                        <Checkbox
                          id={`page-${page.id}`}
                          checked={formData.allowed_pages.includes(page.id)}
                          onCheckedChange={() => handlePageToggle(page.id)}
                          data-testid={`page-checkbox-${page.id}`}
                        />
                        <div>
                          <Label htmlFor={`page-${page.id}`} className="cursor-pointer font-medium">
                            {page.name}
                          </Label>
                          <p className="text-xs text-muted-foreground">{page.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setIsDialogOpen(false)}
                data-testid="cancel-user-btn"
              >
                İptal
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                data-testid="save-user-btn"
              >
                {isEditMode ? "Güncelle" : "Oluştur"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
